import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject, Unsubscribable } from 'rxjs';
import * as _ from 'lodash';

import {
  UserAuth,
  DateFormatterService,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';

import { UserAdministrationService } from '../../../services/user.administration.service';

@Component({
  selector: 'nrwl-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss']
})
export class UserInfoComponent implements OnInit, OnDestroy {
  userName: string;
  userId: string;
  userIsMyself: boolean;
  lastLogin: string;
  userInfo: any = {}; //eslint-disable-line
  userProfile: any = {}; //eslint-disable-line
  currentUser: any; //eslint-disable-line

  private readonly unsubscribe: Subject<void> = new Subject();
  private userInfoUnsubscribable: Unsubscribable;
  private navigationSubscription: Unsubscribable;

  constructor(
    private readonly exchange: ValuesExchangeProvider,
    private readonly dateFormatterService: DateFormatterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userAuth: UserAuth,
    private readonly navigator: NavigationProvider,
    private readonly router: Router,
    private userAdministrationService: UserAdministrationService
  ) {
    this.currentUser = this.userAuth.getUserAuth();
  }

  ngOnInit() {
    this.exchange.notifyListeners('user-profile-loading', true);
    this.userName = this.activatedRoute.snapshot.queryParams['userName'] ?? this.activatedRoute.snapshot.params['userName'] ?? '';
    this.userId = this.activatedRoute.snapshot.queryParams['uid'] ?? '';
    this.userIsMyself = !this.userName || this.currentUser?.data?.USER_ID === this.userName;

    this.getMe();
    this.userInfoUnsubscribable = this.exchange.registerInterest(
      'fusion-user-update-info',
      isDone => {
        if (isDone) {
          this.exchange.notifyListeners('user-profile-loading', true);
          this.getMe();
        }
      }
    );
    //eslint-disable-next-line
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (e.url.indexOf('/profile')) {
          this.exchange.notifyListeners('user-profile-loading', true);
          this.getMe();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.userInfoUnsubscribable) {
      this.userInfoUnsubscribable.unsubscribe();
    }
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  openUpdateUserWizard(wizardId: string): void {
    const userInfo = _.pick(this.userInfo, ['username', 'email', 'name', 'surname', 'id']);
    userInfo['update_user'] = 'update_fusion_sso_user';
    this.navigator.navigateToWizard([wizardId], {
      queryParams: { defaultParams: JSON.stringify(userInfo) }
    });
  }

  isEditable(): boolean {
    return this.userInfo.origin === 'local' || this.userAuth.isAuthedFor('MANAGE_PERMISSIONS');
  }

  private getMe() {
    if (this.userIsMyself) {
      this.userAdministrationService
        .getMe$()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(result => {
          this.userInfo = result.success;
          if ( this.userInfo.origin === 'local' ) {
            this.getUser(this.userInfo.id);
          }
          else {
            this.userAuth.removeRoles(this.userInfo.effectiveRoles);
            this.exchange.notifyListeners('user-profile-info', this.userInfo);
            this.exchange.notifyListeners('user-profile-loading', false);
          }
        }),
        () => {};
    }
    else {
      this.getUser(this.userId);
    }
  }

  private getUser(userId:string) {
    this.userAdministrationService
      .getUser$(userId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        this.userInfo = result.success;
        this.userAuth.removeRoles(this.userInfo.effectiveRoles);
        this._getLastLogin();
        this.exchange.notifyListeners('user-profile-info', this.userInfo);
        this.exchange.notifyListeners('user-profile-loading', false);
      }),
      () => {};
  }

  private _getLastLogin(): void {
    if (this.userInfo.lastLogin) {
      this.lastLogin = this.dateFormatterService.getDateTime(this.userInfo.lastLogin);
    }
  }

}
